import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AdminLayout } from '../AdminLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getDeviceType, DeviceIcon } from '../../components/DeviceIcon';
import { formatDateTimeInBusinessTZ } from '../../utils/date';
import { formatEventSummary } from '../utils/eventSummary';
import { summaryContext, useEntityLookup } from '../utils/eventEntityLookup';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { EventDetailModal, type AppEvent } from '../components/EventDetailModal';

type AnalyticsTab = 'events' | 'statistics' | 'charts';
const ANALYTICS_TABS: readonly AnalyticsTab[] = ['events', 'statistics', 'charts'];

interface UniqueUser {
  id: string;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface EventTypeStats {
  event_type: string;
  count: number;
  success_rate: number;
  avg_processing_time: number;
}

interface SessionEvent {
  id: string;
  event_type: string;
  created_at: string;
  success: boolean;
  page_url: string | null;
  error_message: string | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
}

interface ChartDataPoint {
  date: string;
  count: number;
}

interface ProgramViewData {
  program_name: string;
  views: number;
}

interface ProgramViewMetadata {
  program_name?: string;
  [key: string]: unknown;
}

interface PaymentMethodDistribution {
  payment_method: string;
  count: number;
}

interface DeviceDistribution {
  device: string;
  count: number;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  // Auth
  'auth_login': 'bg-green-100 text-green-800',
  'auth_logout': 'bg-red-100 text-red-800',
  'auth_signup': 'bg-green-100 text-green-800',
  'user_signout': 'bg-red-100 text-red-800',
  // Program
  'program_view': 'bg-blue-100 text-blue-800',
  'program_enroll_start': 'bg-yellow-100 text-yellow-800',
  'program_enroll_complete': 'bg-green-100 text-green-800',
  'program_enroll_abandon': 'bg-orange-100 text-orange-800',
  'program_unenroll': 'bg-orange-100 text-orange-800',
  'program_unenroll_start': 'bg-yellow-100 text-yellow-800',
  'program_unenroll_complete': 'bg-red-100 text-red-800',
  'program_unenroll_failed': 'bg-red-100 text-red-800',
  // Student
  'student_create': 'bg-blue-100 text-blue-800',
  'student_update': 'bg-yellow-100 text-yellow-800',
  'student_delete': 'bg-red-100 text-red-800',
  'waiver_view': 'bg-indigo-100 text-indigo-800',
  'waiver_submit': 'bg-green-100 text-green-800',
  // Flex plans
  'flex_plan_save': 'bg-cyan-100 text-cyan-800',
  // Signups (parent side)
  'signup_registration_create': 'bg-emerald-100 text-emerald-800',
  'signup_registration_cancel': 'bg-orange-100 text-orange-800',
  // Attendance / pickup
  'attendance_absence_set': 'bg-orange-100 text-orange-800',
  'attendance_absence_cleared': 'bg-teal-100 text-teal-800',
  // Red, not teal: this event only fires when a child left with someone unlisted.
  'attendance_pickup_unauthorized': 'bg-red-100 text-red-800',
  'attendance_pickup_recorded': 'bg-teal-100 text-teal-800', // retired name, historical rows
  'authorized_pickup_add': 'bg-teal-100 text-teal-800',
  'authorized_pickup_remove': 'bg-red-100 text-red-800',
  // Payment
  'payment_start': 'bg-purple-100 text-purple-800',
  'payment_complete': 'bg-green-100 text-green-800',
  'payment_failed': 'bg-red-100 text-red-800',
  'payment_abandon': 'bg-orange-100 text-orange-800',
  // Bundle payment (cart) funnel
  'cart_banner_view': 'bg-indigo-50 text-indigo-700',
  'cart_checkout_click': 'bg-indigo-100 text-indigo-800',
  'cart_checkout_start': 'bg-purple-100 text-purple-800',
  'cart_payment_success': 'bg-green-100 text-green-800',
  'cart_payment_failed': 'bg-red-100 text-red-800',
  'add_payment_method': 'bg-purple-100 text-purple-800',
  'update_payment_method': 'bg-purple-100 text-purple-800',
  'autopay_toggle': 'bg-purple-100 text-purple-800',
  'set_default_payment_method': 'bg-purple-100 text-purple-800',
  'autopay_payment': 'bg-purple-100 text-purple-800',
  'payment_reminder_sent': 'bg-orange-100 text-orange-800',
  'late_fee_notice_sent': 'bg-orange-100 text-orange-800',
  'payment_preferences_update': 'bg-purple-100 text-purple-800',
  // Invoice
  'invoice_view': 'bg-indigo-100 text-indigo-800',
  'invoice_download': 'bg-indigo-100 text-indigo-800',
  'invoice_voided': 'bg-red-100 text-red-800',
  'recurring_invoice': 'bg-indigo-100 text-indigo-800',
  // Coupon
  'coupon_apply': 'bg-yellow-100 text-yellow-800',
  'coupon_remove': 'bg-yellow-100 text-yellow-800',
  // Brand profile
  'brand_switch': 'bg-teal-100 text-teal-800',
  // Co-parent
  'co_parent_invite_sent': 'bg-violet-100 text-violet-800',
  'co_parent_access_granted': 'bg-violet-100 text-violet-800',
  'co_parent_invite_accepted': 'bg-emerald-100 text-emerald-800',
  // Admin
  'admin_program_create': 'bg-slate-100 text-slate-800',
  'admin_program_update': 'bg-slate-100 text-slate-800',
  'admin_program_delete': 'bg-slate-100 text-slate-800',
  'admin_user_create': 'bg-slate-100 text-slate-800',
  'admin_user_update': 'bg-slate-100 text-slate-800',
  'admin_coupon_create': 'bg-slate-100 text-slate-800',
  'admin_refund_process': 'bg-slate-100 text-slate-800',
  'admin_manual_payment': 'bg-slate-100 text-slate-800',
  'admin_invoice_edit': 'bg-slate-100 text-slate-800',
  'admin_late_fee_added': 'bg-slate-100 text-slate-800',
  'admin_late_fee_waived': 'bg-slate-100 text-slate-800',
  'admin_enrollment_status_toggle': 'bg-slate-100 text-slate-800',
  'admin_enrollment_edit': 'bg-slate-100 text-slate-800',
  'admin_enrollment_unenroll': 'bg-slate-100 text-slate-800',
  'admin_bulk_deactivate_enrollments': 'bg-slate-100 text-slate-800',
  'admin_signup_event_create': 'bg-slate-100 text-slate-800',
  'admin_signup_event_edit': 'bg-slate-100 text-slate-800',
  'admin_signup_event_regenerate_slots': 'bg-slate-100 text-slate-800',
  'admin_signup_event_duplicate': 'bg-slate-100 text-slate-800',
  'admin_signup_event_delete': 'bg-slate-100 text-slate-800',
  'admin_signup_registration_delete': 'bg-slate-100 text-slate-800',
  'admin_coupon_grant': 'bg-slate-100 text-slate-800',
  'admin_printable_coupons_create': 'bg-slate-100 text-slate-800',
  'admin_eligible_rule_create': 'bg-slate-100 text-slate-800',
  'admin_eligible_rule_update': 'bg-slate-100 text-slate-800',
  'admin_eligible_rule_delete': 'bg-slate-100 text-slate-800',
  'admin_teacher_create': 'bg-slate-100 text-slate-800',
  'admin_teacher_update': 'bg-slate-100 text-slate-800',
  'admin_teacher_delete': 'bg-slate-100 text-slate-800',
  'admin_email_template_update': 'bg-slate-100 text-slate-800',
  'admin_email_test_send': 'bg-slate-100 text-slate-800',
  'admin_line_item_config_create': 'bg-slate-100 text-slate-800',
  'admin_line_item_config_update': 'bg-slate-100 text-slate-800',
  'admin_line_item_config_delete': 'bg-slate-100 text-slate-800',
  'admin_student_photo_import': 'bg-slate-100 text-slate-800',
  'student_parent_link': 'bg-violet-100 text-violet-800',
  'student_parent_unlink': 'bg-red-100 text-red-800',
  // Parent Genie
  'parent_agent_query': 'bg-blue-100 text-blue-800',
  'parent_agent_feedback': 'bg-blue-100 text-blue-800',
  // System
  'agent_query': 'bg-amber-100 text-amber-800',
  'feature_flag_override': 'bg-amber-100 text-amber-800',
  'error_occurred': 'bg-red-100 text-red-800',
  'stripe_webhook': 'bg-amber-100 text-amber-800',
  'zelle_email': 'bg-amber-100 text-amber-800',
  'view_faq': 'bg-gray-100 text-gray-800',
};

function getEventTypeColor(eventType: string) {
  return EVENT_TYPE_COLORS[eventType] ?? 'bg-gray-100 text-gray-800';
}




const AdminAnalytics: React.FC = () => {
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
  const [userSearchInput, setUserSearchInput] = useState<string>('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [eventTypeDropdownOpen, setEventTypeDropdownOpen] = useState(false);
  const [sessionModalId, setSessionModalId] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  /**
   * Filters to everything that ever touched one entity — an invoice, a student, an
   * enrollment. `entity_id` has been written by nearly every emitter since the table
   * existed but was never queryable from here, which is the shape most support questions
   * arrive in ("what happened to this invoice?").
   */
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [failuresOnly, setFailuresOnly] = useState<boolean>(false);
  /**
   * Teacher-portal events land with `user_type='staff'`, which nothing on this page could
   * select for — the attendance and check-out trail was in the table but not reachable.
   */
  const [selectedUserType, setSelectedUserType] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(50);
const [currentPage, setCurrentPage] = useState<number>(1);
  const [statsTimePeriod, setStatsTimePeriod] = useState<string>('last_week');
  const [activeTab, setActiveTab] = useUrlTabState<AnalyticsTab>('tab', ANALYTICS_TABS, 'events');
  const [chartTimePeriod, setChartTimePeriod] = useState<string>('last_30_days');

  // Helper function to get date range for statistics
  const getStatsDateRange = (period: string) => {
    const now = new Date();
    let startDate: Date;

    switch (period) {
      case 'last_hour':
        startDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case 'last_24_hours':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'last_week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'last_month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    return {
      start: startDate.toISOString(),
      end: now.toISOString()
    };
  };

  // Helper function to get date range for charts
  const getChartDateRange = (period: string) => {
    const now = new Date();
    let startDate: Date;
    let timeUnit: string;

    switch (period) {
      case 'last_24_hours':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        timeUnit = 'hour';
        break;
      case 'last_7_days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        timeUnit = 'day';
        break;
      case 'last_30_days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        timeUnit = 'day';
        break;
      default:
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        timeUnit = 'day';
    }

    return {
      start: startDate.toISOString(),
      end: now.toISOString(),
      timeUnit
    };
  };

  // Fetch event type statistics
  const { data: eventTypeStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['eventTypeStats', statsTimePeriod],
    queryFn: async (): Promise<EventTypeStats[]> => {
      const statsRange = getStatsDateRange(statsTimePeriod);
      const { data, error } = await supabase
        .from('app_events')
        .select('event_type, success, processing_time_ms')
        .gte('created_at', statsRange.start)
        .lte('created_at', statsRange.end)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Group by event type and calculate stats
      const stats: { [key: string]: { total: number; success: number; times: number[] } } = {};
      
      data.forEach(event => {
        if (!stats[event.event_type]) {
          stats[event.event_type] = { total: 0, success: 0, times: [] };
        }
        stats[event.event_type].total++;
        if (event.success) stats[event.event_type].success++;
        if (event.processing_time_ms) stats[event.event_type].times.push(event.processing_time_ms);
      });

      return Object.entries(stats).map(([event_type, stat]) => ({
        event_type,
        count: stat.total,
        success_rate: (stat.success / stat.total) * 100,
        avg_processing_time: stat.times.length > 0 ? stat.times.reduce((a, b) => a + b, 0) / stat.times.length : 0
      })).sort((a, b) => b.count - a.count);
    }
  });

  // Fetch recent events with pagination
  const { data: recentEventsData, isLoading: isLoadingEvents } = useQuery({
    queryKey: ['recentEvents', selectedUserEmail, dateRange, selectedEventType, selectedEntityId, failuresOnly, selectedUserType, pageSize, currentPage],
    queryFn: async (): Promise<{ events: AppEvent[]; totalCount: number }> => {
      // The count and the page must be filtered identically, or the pager lies. One helper
      // applied to both builders — `.eq()` returns the same builder, so this stays typed.
      const applyFilters = <T extends { eq(column: string, value: string | boolean): T }>(query: T): T => {
        let q = query;
        if (selectedUserEmail) q = q.eq('email', selectedUserEmail);
        if (selectedEventType) q = q.eq('event_type', selectedEventType);
        if (selectedEntityId) q = q.eq('entity_id', selectedEntityId);
        if (selectedUserType) q = q.eq('user_type', selectedUserType);
        if (failuresOnly) q = q.eq('success', false);
        return q;
      };

      const countQuery = applyFilters(
        supabase
          .from('app_events')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', `${dateRange.start}T00:00:00`)
          .lte('created_at', `${dateRange.end}T23:59:59`)
      );

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      const eventsQuery = applyFilters(
        supabase
          .from('app_events')
          .select('*')
          .gte('created_at', `${dateRange.start}T00:00:00`)
          .lte('created_at', `${dateRange.end}T23:59:59`)
      );

      const offset = (currentPage - 1) * pageSize;
      const { data: events, error: eventsError } = await eventsQuery
        .order('created_at', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (eventsError) throw eventsError;

      return {
        events: events || [],
        totalCount: count || 0
      };
    }
  });

  const recentEvents = recentEventsData?.events || [];
  // Names for the ids this page of events points at; see `useEntityLookup`.
  const eventLookup = useEntityLookup(recentEvents);
  const totalCount = recentEventsData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Reset current page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedUserEmail, dateRange, selectedEventType, selectedEntityId, failuresOnly, selectedUserType, pageSize]);

  // Fetch session events for selected session
  const { data: sessionEvents, isLoading: isLoadingSession } = useQuery({
    queryKey: ['sessionEvents', sessionModalId],
    queryFn: async (): Promise<SessionEvent[]> => {
      if (!sessionModalId) return [];

      const { data, error } = await supabase
        .from('app_events')
        .select('id, event_type, created_at, success, page_url, error_message, entity_type, entity_id, metadata')
        .eq('session_id', sessionModalId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!sessionModalId
  });

  const sessionLookup = useEntityLookup(sessionEvents);

  // Chart data queries
  const { data: authLoginTrend } = useQuery({
    queryKey: ['authLoginTrend', chartTimePeriod],
    queryFn: async (): Promise<ChartDataPoint[]> => {
      const range = getChartDateRange(chartTimePeriod);
      
      const { data, error } = await supabase
        .from('app_events')
        .select('created_at')
        .eq('event_type', 'auth_login')
        .eq('success', true)
        .gte('created_at', range.start)
        .lte('created_at', range.end)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by time unit
      const grouped: { [key: string]: number } = {};
      data.forEach(event => {
        const date = new Date(event.created_at);
        let key: string;
        
        if (range.timeUnit === 'hour') {
          key = `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:00`;
        } else {
          key = `${date.getMonth() + 1}/${date.getDate()}`;
        }
        
        grouped[key] = (grouped[key] || 0) + 1;
      });

      return Object.entries(grouped).map(([date, count]) => ({ date, count }));
    }
  });

  const { data: paymentCompleteTrend } = useQuery({
    queryKey: ['paymentCompleteTrend', chartTimePeriod],
    queryFn: async (): Promise<ChartDataPoint[]> => {
      const range = getChartDateRange(chartTimePeriod);
      const { data, error } = await supabase
        .from('app_events')
        .select('created_at')
        .eq('event_type', 'payment_complete')
        .eq('success', true)
        .gte('created_at', range.start)
        .lte('created_at', range.end)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const grouped: { [key: string]: number } = {};
      data.forEach(event => {
        const date = new Date(event.created_at);
        let key: string;
        
        if (range.timeUnit === 'hour') {
          key = `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:00`;
        } else {
          key = `${date.getMonth() + 1}/${date.getDate()}`;
        }
        
        grouped[key] = (grouped[key] || 0) + 1;
      });

      return Object.entries(grouped).map(([date, count]) => ({ date, count }));
    }
  });

  const { data: addPaymentMethodTrend } = useQuery({
    queryKey: ['addPaymentMethodTrend', chartTimePeriod],
    queryFn: async (): Promise<ChartDataPoint[]> => {
      const range = getChartDateRange(chartTimePeriod);
      const { data, error } = await supabase
        .from('app_events')
        .select('created_at')
        .eq('event_type', 'add_payment_method')
        .eq('success', true)
        .gte('created_at', range.start)
        .lte('created_at', range.end)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const grouped: { [key: string]: number } = {};
      data.forEach(event => {
        const date = new Date(event.created_at);
        let key: string;
        
        if (range.timeUnit === 'hour') {
          key = `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:00`;
        } else {
          key = `${date.getMonth() + 1}/${date.getDate()}`;
        }
        
        grouped[key] = (grouped[key] || 0) + 1;
      });

      return Object.entries(grouped).map(([date, count]) => ({ date, count }));
    }
  });

  const { data: topPrograms } = useQuery({
    queryKey: ['topPrograms', chartTimePeriod],
    queryFn: async (): Promise<ProgramViewData[]> => {
      const range = getChartDateRange(chartTimePeriod);
      const { data, error } = await supabase
        .from('app_events')
        .select('metadata')
        .eq('event_type', 'program_view')
        .eq('success', true)
        .gte('created_at', range.start)
        .lte('created_at', range.end);

      if (error) throw error;

      const programCounts: { [key: string]: number } = {};
      data.forEach(event => {
        if (event.metadata && typeof event.metadata === 'object') {
          const metadata = event.metadata as ProgramViewMetadata;
          const programName = metadata.program_name;
          if (programName && typeof programName === 'string') {
            programCounts[programName] = (programCounts[programName] || 0) + 1;
          }
        }
      });

      return Object.entries(programCounts)
        .map(([program_name, views]) => ({ program_name, views }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 10);
    }
  });

    const { data: dailyActiveUsers } = useQuery({
    queryKey: ['dailyActiveUsers', chartTimePeriod],
    queryFn: async (): Promise<ChartDataPoint[]> => {
      const range = getChartDateRange(chartTimePeriod);
      
      const { data, error } = await supabase.rpc('get_daily_active_users', {
        start_date: range.start,
        end_date: range.end
      });

      if (error) throw error;

      // The RPC function already returns the exact format we need
      return data || [];
    }
  });

  const { data: paymentMethodDistribution } = useQuery({
    queryKey: ['paymentMethodDistribution', chartTimePeriod],
    queryFn: async (): Promise<PaymentMethodDistribution[]> => {
      const range = getChartDateRange(chartTimePeriod);
      const { data, error } = await supabase
        .from('payments')
        .select('payment_method')
        .eq('status', 'completed')
        .gte('created_at', range.start)
        .lte('created_at', range.end);

      if (error) throw error;

      const counts: { [key: string]: number } = {};
      data.forEach(payment => {
        const method = payment.payment_method || 'unknown';
        counts[method] = (counts[method] || 0) + 1;
      });

      return Object.entries(counts).map(([payment_method, count]) => ({
        payment_method,
        count
      }));
    }
  });

  const { data: deviceDistribution } = useQuery({
    queryKey: ['deviceDistribution', chartTimePeriod],
    queryFn: async (): Promise<DeviceDistribution[]> => {
      const range = getChartDateRange(chartTimePeriod);
      const { data, error } = await supabase
        .from('app_events')
        .select('user_agent')
        .gte('created_at', range.start)
        .lte('created_at', range.end);

      if (error) throw error;
      
      const getOs = (ua: string | null): string => {
        if (!ua) return 'Unknown';
        if (/android/i.test(ua)) return 'Android';
        if (/iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream) return 'iOS';
        if (/mac/i.test(ua)) return 'macOS';
        if (/win/i.test(ua)) return 'Windows';
        if (/linux/i.test(ua)) return 'Linux';
        return 'Other';
      };

      const counts: { [key: string]: number } = {};
      data.forEach(event => {
        const os = getOs(event.user_agent);
        counts[os] = (counts[os] || 0) + 1;
      });

      return Object.entries(counts).filter(([device]) => device !== 'Unknown').map(([device, count]) => ({
        device,
        count
      }));
    }
  });

  // Fetch unique users and sessions for filtering
  const { data: uniqueUsers } = useQuery({
    queryKey: ['allParents'],
    queryFn: async (): Promise<UniqueUser[]> => {
      const { data: parents, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email')
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true });

      if (error) throw error;

      return (parents || []).map(p => ({
        id: p.id as string,
        email: p.email as string | null,
        first_name: p.first_name as string | null,
        last_name: p.last_name as string | null,
      }));
    }
  });

  /**
   * The event-type vocabulary, read from the events themselves.
   *
   * This list used to be a hardcoded array, and it drifted in both directions: a dozen
   * emitted types were missing (unfilterable), and seven listed types had never been emitted
   * by anything. `get_app_event_types()` groups the last 365 days of `app_events`.
   *
   * Falls back to the static list below if the RPC is absent — the patch adding it is applied
   * separately from this deploy, and an analytics filter is not worth a broken page.
   */
  const { data: observedEventTypes } = useQuery<string[] | null>({
    queryKey: ['app-event-types'],
    // The vocabulary changes when a release adds an event, not minute to minute.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_event_types');
      if (error) return null;
      return ((data ?? []) as { event_type: string }[]).map(row => row.event_type);
    },
  });

  const fallbackEventTypes = useMemo(() => [
    // Auth
    'auth_login', 'auth_logout', 'auth_signup', 'user_signout',
    // Program
    'program_view', 'program_enroll_start', 'program_enroll_complete', 'program_enroll_abandon',
    'program_unenroll', 'program_unenroll_start', 'program_unenroll_complete', 'program_unenroll_failed',
    // Student
    'student_create', 'student_update', 'student_delete', 'waiver_view', 'waiver_submit',
    // Attendance / pickup
    'attendance_absence_set', 'attendance_absence_cleared', 'attendance_pickup_unauthorized',
    'authorized_pickup_add', 'authorized_pickup_remove',
    // Payment
    'payment_start', 'payment_complete', 'payment_failed', 'payment_abandon',
    'cart_banner_view', 'cart_checkout_click', 'cart_checkout_start', 'cart_payment_success', 'cart_payment_failed',
    'add_payment_method', 'update_payment_method',
    'autopay_toggle', 'set_default_payment_method', 'autopay_payment', 'payment_reminder_sent',
    'late_fee_notice_sent',
    'payment_preferences_update',
    // Invoice
    'invoice_view', 'invoice_download', 'invoice_voided', 'recurring_invoice',
    // Coupon
    'coupon_apply', 'coupon_remove',
    // Brand profile
    'brand_switch',
    // Co-parent
    'co_parent_invite_sent', 'co_parent_access_granted', 'co_parent_invite_accepted',
    // Admin - programs
    'admin_program_create', 'admin_program_update', 'admin_program_delete',
    // Admin - users & coupons
    'admin_user_create', 'admin_user_update', 'admin_coupon_create',
    // Admin - finances
    'admin_refund_process', 'admin_manual_payment', 'admin_invoice_edit',
    'admin_late_fee_added', 'admin_late_fee_waived',
    // Admin - enrollments
    'admin_enrollment_status_toggle', 'admin_enrollment_edit', 'admin_bulk_deactivate_enrollments',
    'admin_enrollment_unenroll',
    // Flex plans
    'flex_plan_save',
    // Signups (parent side)
    'signup_registration_create', 'signup_registration_cancel',
    // Admin - signups
    'admin_signup_event_create', 'admin_signup_event_edit', 'admin_signup_event_regenerate_slots',
    'admin_signup_event_duplicate', 'admin_signup_event_delete', 'admin_signup_registration_delete',
    // Parent Genie
    'parent_agent_query', 'parent_agent_feedback',
    // System
    'agent_query', 'feature_flag_override', 'error_occurred', 'stripe_webhook', 'zelle_email', 'view_faq',
  ], []);

  const eventTypes = useMemo(
    () => (observedEventTypes && observedEventTypes.length > 0 ? observedEventTypes : fallbackEventTypes),
    [observedEventTypes, fallbackEventTypes]
  );

  const filteredEventTypes = useMemo(() =>
    selectedEventType
      ? eventTypes.filter(t => t.toLowerCase().includes(selectedEventType.toLowerCase()))
      : eventTypes,
    [selectedEventType, eventTypes]
  );

  const filteredUsers = useMemo(() => {
    if (!userSearchInput) return uniqueUsers || [];
    const q = userSearchInput.toLowerCase();
    return (uniqueUsers || []).filter(u =>
      u.email?.toLowerCase().includes(q) ||
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q) ||
      `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase().includes(q)
    );
  }, [userSearchInput, uniqueUsers]);

  const formatDate = (dateString: string) => {
    return formatDateTimeInBusinessTZ(dateString);
  };

  /**
   * Opens a row's full content, unless the click landed on one of the row's own filter or
   * session buttons, or ended a drag that selected text (copying an id out of the row).
   */
  const openEventDetail = (e: React.MouseEvent, event: AppEvent) => {
    if ((e.target as Element).closest('button')) return;
    if (window.getSelection()?.toString()) return;
    setSelectedEvent(event);
  };

  const getDeviceDisplayLabel = (event: AppEvent): string => {
    if (!event.user_agent || event.user_agent.trim() === '') {
      return 'Server';
    }

    return getDeviceType(event);
  };

  return (
    <AdminLayout>
      <div className="space-y-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('events')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'events'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Events
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'statistics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Stats
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'charts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Trends
          </button>
        </nav>
      </div>

            {/* Tab Content */}
      {/* Statistics Tab */}
      {activeTab === 'statistics' && (
        <div className="space-y-6">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold">Stats</h2>
              <div className="flex border rounded-lg overflow-hidden flex-shrink-0">
                {[
                  { key: 'last_hour', label: '1h' },
                  { key: 'last_24_hours', label: '24h' },
                  { key: 'last_week', label: 'Week' },
                  { key: 'last_month', label: 'Month' }
                ].map((period) => (
                  <button
                    key={period.key}
                    onClick={() => setStatsTimePeriod(period.key)}
                    className={`px-3 py-1 text-sm font-medium transition-colors ${
                      statsTimePeriod === period.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">Portal activity across both brands. BHCS and Kaleido share one portal, so these events have no brand.</p>
            {isLoadingStats ? (
              <div>Loading statistics...</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {eventTypeStats?.map((stat, index) => (
                  <div key={`stat_${stat.event_type}_${index}`} className="border rounded p-2">
                    <div className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-semibold truncate max-w-full ${getEventTypeColor(stat.event_type)}`}>
                      {stat.event_type}
                    </div>
                    <div className="mt-1">
                      <div className="text-xl font-bold leading-tight">{stat.count}</div>
                      <div className="text-xs text-gray-500">{stat.success_rate.toFixed(0)}% success</div>
                      {stat.avg_processing_time > 0 && (
                        <div className="text-xs text-gray-400">{stat.avg_processing_time.toFixed(0)}ms avg</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Charts Tab */}
      {activeTab === 'charts' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Charts & Trends</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Time period:</span>
              <div className="flex border rounded-lg overflow-hidden">
                {[
                  { key: 'last_24_hours', label: '24 Hours' },
                  { key: 'last_7_days', label: '7 Days' },
                  { key: 'last_30_days', label: '30 Days' }
                ].map((period) => (
                  <button
                    key={period.key}
                    onClick={() => setChartTimePeriod(period.key)}
                    className={`px-3 py-1 text-sm font-medium transition-colors ${
                      chartTimePeriod === period.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-4">Portal activity across both brands. BHCS and Kaleido share one portal, so these events have no brand. Payment Methods counts payments for both brands' programs.</p>
          
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Auth Login Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={authLoginTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Payment Complete Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={paymentCompleteTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Add Payment Method Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={addPaymentMethodTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Top Viewed Programs</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topPrograms || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ program_name, views }) => `${program_name}: ${views}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="views"
                    >
                      {topPrograms?.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Payment Methods</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethodDistribution || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ payment_method, count }) => `${payment_method}: ${count}`}
                    >
                      {paymentMethodDistribution?.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Client Devices</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deviceDistribution || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#82ca9d"
                      dataKey="count"
                      label={({ device, count }) => `${device}: ${count}`}
                    >
                      {deviceDistribution?.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 80}, 60%, 60%)`} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-md font-semibold mb-2">Daily Active Users</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyActiveUsers || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      )}


{activeTab === 'events' && (
        <div className="space-y-6">
          {/* Filters — always visible, 2-column layout */}
          <div className="pb-3 border-b border-gray-200 space-y-2">
            {/* Row 1: dates */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="dateStart" className="text-xs font-medium text-gray-600">Start Date</label>
                <input
                  id="dateStart"
                  type="date"
                  className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="dateEnd" className="text-xs font-medium text-gray-600">End Date</label>
                <input
                  id="dateEnd"
                  type="date"
                  className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
              </div>
            </div>
            {/* Row 2: event type + user */}
            <div className="grid grid-cols-2 gap-2 items-end">
              <div className="relative">
                <label htmlFor="eventType" className="text-xs font-medium text-gray-600">Event Type</label>
                <input
                  id="eventType"
                  type="text"
                  className="w-full px-2 py-1 border rounded text-sm border-gray-300"
                  placeholder="All event types…"
                  value={selectedEventType}
                  onChange={(e) => { setSelectedEventType(e.target.value); setEventTypeDropdownOpen(true); }}
                  onFocus={() => setEventTypeDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setEventTypeDropdownOpen(false), 150)}
                  autoComplete="off"
                />
                {eventTypeDropdownOpen && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                    {selectedEventType && (
                      <div
                        className="py-1.5 px-3 text-xs text-gray-500 cursor-pointer hover:bg-gray-100"
                        onMouseDown={() => { setSelectedEventType(''); setEventTypeDropdownOpen(false); }}
                      >
                        Clear — show all
                      </div>
                    )}
                    {filteredEventTypes.map(type => (
                      <div
                        key={type}
                        className="py-1.5 px-3 text-sm cursor-pointer hover:bg-blue-50"
                        onMouseDown={() => { setSelectedEventType(type); setEventTypeDropdownOpen(false); }}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <label htmlFor="userId" className="text-xs font-medium text-gray-600">User</label>
                <input
                  id="userId"
                  type="text"
                  className="w-full px-2 py-1 border rounded text-sm border-gray-300"
                  placeholder="Name or email…"
                  value={userSearchInput}
                  onChange={(e) => { setUserSearchInput(e.target.value); setSelectedUserEmail(''); setUserDropdownOpen(true); }}
                  onFocus={() => setUserDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setUserDropdownOpen(false), 150)}
                  autoComplete="off"
                />
                {userDropdownOpen && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                    {userSearchInput && (
                      <div
                        className="py-1.5 px-3 text-xs text-gray-500 cursor-pointer hover:bg-gray-100"
                        onMouseDown={() => { setSelectedUserEmail(''); setUserSearchInput(''); setUserDropdownOpen(false); }}
                      >
                        Clear — show all
                      </div>
                    )}
                    {filteredUsers.map((user) => {
                      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
                      return (
                        <div
                          key={user.id}
                          className="py-1.5 px-3 text-sm cursor-pointer hover:bg-blue-50"
                          onMouseDown={() => {
                            setSelectedUserEmail(user.email || '');
                            setUserSearchInput(fullName || user.email || '');
                            setUserDropdownOpen(false);
                          }}
                        >
                          {fullName && <span className="font-medium">{fullName}</span>}
                          {user.email && (
                            <span className={fullName ? ' text-xs text-gray-500 ml-1' : ''}>
                              {user.email}
                            </span>
                          )}
                          {!fullName && !user.email && <span className="text-gray-400">Unknown</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {/* Row 3: entity + failures */}
            <div className="grid grid-cols-2 gap-2 items-end">
              <div>
                <label htmlFor="entityId" className="text-xs font-medium text-gray-600">Entity ID</label>
                <input
                  id="entityId"
                  type="text"
                  className="w-full px-2 py-1 border rounded text-sm border-gray-300"
                  placeholder="Invoice, student, enrollment…"
                  value={selectedEntityId}
                  onChange={(e) => setSelectedEntityId(e.target.value.trim())}
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor="userType" className="text-xs font-medium text-gray-600">Actor</label>
                <select
                  id="userType"
                  className="w-full px-2 py-1 border rounded text-sm border-gray-300 bg-white"
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                >
                  <option value="">Anyone</option>
                  <option value="parent">Parent</option>
                  <option value="admin">Admin</option>
                  <option value="staff">Teacher (staff)</option>
                  <option value="system">System</option>
                  <option value="anonymous">Anonymous</option>
                </select>
              </div>
            </div>
            {/* Row 4: failures */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={failuresOnly}
                  onChange={(e) => setFailuresOnly(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Failures only
              </label>
            </div>
            {/* Clear */}
            {(selectedUserEmail || selectedEventType || selectedEntityId || failuresOnly || selectedUserType) && (
              <button
                onClick={() => {
                  setSelectedUserEmail('');
                  setUserSearchInput('');
                  setSelectedEventType('');
                  setSelectedEntityId('');
                  setFailuresOnly(false);
                  setSelectedUserType('');
                }}
                className="text-xs text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>

          <Card className="p-3 md:p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm text-gray-600">
                <span className="hidden md:inline">Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} events</span>
                <span className="md:hidden">{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount}</span>
              </span>
              <div className="flex items-center gap-2">
                <label htmlFor="pageSize" className="text-sm text-gray-600">Per page:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            {isLoadingEvents ? (
              <div>Loading events...</div>
            ) : (
              <>
                {/* Desktop table layout */}
                <div className="hidden md:block overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Event Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Entity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Device</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Session</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Details</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentEvents?.map((event, index) => (
                        <tr
                          key={`recent_event_${event.id}_${index}`}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={(e) => openEventDetail(e, event)}
                        >
                          <td className="px-6 py-3">
                            <button
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getEventTypeColor(event.event_type)} hover:opacity-75`}
                              onClick={() => setSelectedEventType(event.event_type)}
                            >
                              {event.event_type}
                            </button>
                            {!event.success && (
                              <span className="ml-1.5 inline-flex px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                Failed
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm">
                              {event.email ? (
                                <button
                                  onClick={() => { setSelectedUserEmail(event.email!); setUserSearchInput(event.email!); }}
                                  className="text-blue-600 hover:underline"
                                >
                                  {event.email}
                                </button>
                              ) : (
                                <span className="text-gray-500">Anonymous</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {event.user_type}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            {event.entity_id ? (
                              <button
                                onClick={() => setSelectedEntityId(event.entity_id!)}
                                className="text-left text-blue-600 hover:underline"
                                title={event.entity_id}
                              >
                                <div className="text-sm">{event.entity_type || 'entity'}</div>
                                <div className="text-xs text-gray-500 font-mono">{event.entity_id.slice(0, 8)}…</div>
                              </button>
                            ) : event.entity_type ? (
                              <span className="text-sm text-gray-500">{event.entity_type}</span>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm flex items-center space-x-2">
                              <DeviceIcon deviceType={getDeviceType(event)} />
                              <span>{getDeviceDisplayLabel(event)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            {event.session_id ? (
                              <button
                                onClick={() => setSessionModalId(event.session_id!)}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {formatDate(event.created_at)}
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-700">
                              {formatEventSummary(event.event_type, event.metadata, summaryContext(event, eventLookup)) || '-'}
                            </div>
                            {/* The column, not the metadata copy: emitters that set only
                                `errorMessage` had no way to show why a write failed. */}
                            {event.error_message && (
                              <div className="text-xs text-red-600 mt-0.5 break-words">{event.error_message}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile card layout */}
                <div className="md:hidden space-y-3">
                  {recentEvents?.map((event, index) => (
                    <div
                      key={`recent_event_mobile_${event.id}_${index}`}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={(e) => openEventDetail(e, event)}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getEventTypeColor(event.event_type)} hover:opacity-75`}
                            onClick={() => setSelectedEventType(event.event_type)}
                          >
                            {event.event_type}
                          </button>
                          {!event.success && (
                            <span className="inline-flex px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                              Failed
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(event.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="text-sm">
                          {event.email ? (
                            <button
                              onClick={() => { setSelectedUserEmail(event.email!); setUserSearchInput(event.email!); }}
                              className="text-blue-600 hover:underline break-all"
                            >
                              {event.email}
                            </button>
                          ) : (
                            <span className="text-gray-500">Anonymous</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{event.user_type}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <DeviceIcon deviceType={getDeviceType(event)} />
                          {getDeviceDisplayLabel(event)}
                        </span>
                        {event.entity_id && (
                          <button
                            onClick={() => setSelectedEntityId(event.entity_id!)}
                            className="text-blue-600 hover:underline"
                            title={event.entity_id}
                          >
                            {event.entity_type || 'entity'} {event.entity_id.slice(0, 8)}…
                          </button>
                        )}
                        {event.session_id && (
                          <button
                            onClick={() => setSessionModalId(event.session_id!)}
                            className="text-blue-600 hover:underline"
                          >
                            View session
                          </button>
                        )}
                      </div>
                      {formatEventSummary(event.event_type, event.metadata, summaryContext(event, eventLookup)) && (
                        <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded px-2 py-1">
                          {formatEventSummary(event.event_type, event.metadata, summaryContext(event, eventLookup))}
                        </div>
                      )}
                      {event.error_message && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1 break-words">
                          {event.error_message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Pagination */}
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-2 flex-wrap justify-center">
                <Button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  First
                </Button>
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>

                {/* Show up to 5 page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return pageNum <= totalPages ? (
                    <Button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                    >
                      {pageNum}
                    </Button>
                  ) : null;
                })}

                <Button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
                <Button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Last
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
      </div>

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          summary={formatEventSummary(selectedEvent.event_type, selectedEvent.metadata, summaryContext(selectedEvent, eventLookup))}
          badgeClassName={getEventTypeColor(selectedEvent.event_type)}
          onClose={() => setSelectedEvent(null)}
          onViewSession={(sessionId) => { setSelectedEvent(null); setSessionModalId(sessionId); }}
        />
      )}

      {/* Session flow modal — rendered via portal so it escapes any overflow:hidden ancestor */}
      {sessionModalId && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex flex-col justify-end sm:items-center sm:justify-center bg-black bg-opacity-40"
          onClick={(e) => { if (e.target === e.currentTarget) setSessionModalId(''); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setSessionModalId(''); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] sm:mx-4 flex flex-col">
            <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h2 className="text-base font-semibold">Session Flow</h2>
                {!sessionModalId.startsWith('__none__') && (
                  <p className="text-xs text-gray-500 font-mono mt-0.5">{sessionModalId}</p>
                )}
              </div>
              <button
                onClick={() => setSessionModalId('')}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {sessionModalId.startsWith('__none__') ? (
                <div className="text-sm text-gray-500 py-4 text-center">No session ID recorded for this event.</div>
              ) : isLoadingSession ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : (
                sessionEvents?.map((event, index) => {
                  const summary = formatEventSummary(event.event_type, event.metadata, summaryContext(event, sessionLookup));
                  return (
                    <div key={`modal_event_${event.id}_${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getEventTypeColor(event.event_type)}`}>
                            {event.event_type}
                          </span>
                          {!event.success && (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">Failed</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{formatDate(event.created_at)}</div>
                        {summary && <div className="text-sm text-gray-700 mt-0.5">{summary}</div>}
                        {event.error_message && (
                          <div className="text-xs text-red-600 mt-0.5 break-words">{event.error_message}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
};

export default AdminAnalytics;